// Web implementation of `react-native-nmap` (Naver Map) backed by the Naver Maps
// JS API v3. Mirrors the exact surface the app uses so `store/map.ts` and
// `ui/common/default-map-view.tsx` / markers stay unchanged:
//   - <NaverMapView ref center onCameraChange onMapClick onInitialized>
//     imperative ref: animateToRegion(morph) / animateToCoordinate(panTo) /
//     setLocationTrackingMode / animateToTwoCoordinates
//   - <Marker coordinate image width height anchor caption onClick />
//   - Align enum
//
// Auth: gogo-client 는 구 "AI·NAVER API" 콘솔 앱(X-NCP-APIGW-API-KEY-ID)이라
// 스크립트 파라미터가 ncpClientId 다. 신규 Maps 콘솔용 ncpKeyId 를 쓰면
// go-go.space origin 에서도 401 인증 실패 (2026-07-16 실측: ncpClientId=성공,
// ncpKeyId=실패). 도메인은 NCP 콘솔 Web 서비스 URL 에 등록돼 있어야 한다
// (go-go.space 등록됨; 미등록 origin 은 navermap_authFailure 로 graceful 처리).
import React, {
  createContext,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from 'react'
import { View, StyleSheet } from 'react-native'

const NCP_CLIENT_ID = 'r0o0yyageu'
const SCRIPT_SRC = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${NCP_CLIENT_ID}`

let _loadPromise = null
function loadNaverMaps() {
  if (typeof window === 'undefined')
    return Promise.reject(new Error('no window'))
  if (window.naver && window.naver.maps)
    return Promise.resolve(window.naver.maps)
  if (_loadPromise) return _loadPromise
  window.navermap_authFailure = () => {
    console.warn(
      '[naver-map] auth failed — current origin must be a registered Web 서비스 URL in NCP (go-go.space is; localhost is not).',
    )
  }
  _loadPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = SCRIPT_SRC
    s.async = true
    s.onload = () => resolve(window.naver.maps)
    s.onerror = () => reject(new Error('naver maps script failed to load'))
    document.head.appendChild(s)
  })
  return _loadPromise
}

// delta (constant.ts: longitudeDelta = 360 / 2^zoom) -> naver integer zoom level.
function deltaToZoom(region) {
  const d = region && region.longitudeDelta
  if (!d || d <= 0) return 15
  const z = Math.log2(360 / d)
  return Math.max(1, Math.min(21, Math.round(z)))
}

function boundsToCoveringRegion(b) {
  const ne = b.getNE ? b.getNE() : b.getMax()
  const sw = b.getSW ? b.getSW() : b.getMin()
  return [
    { latitude: sw.lat(), longitude: sw.lng() },
    { latitude: ne.lat(), longitude: sw.lng() },
    { latitude: ne.lat(), longitude: ne.lng() },
    { latitude: sw.lat(), longitude: ne.lng() },
  ]
}

const MapContext = createContext(null)

export const NaverMapView = forwardRef(function NaverMapView(props, ref) {
  const { center, style, onCameraChange, onMapClick, onInitialized, children } =
    props
  const elRef = useRef(null)
  const mapRef = useRef(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let listeners = []
    let cancelled = false
    loadNaverMaps()
      .then((maps) => {
        if (cancelled || !elRef.current) return
        const map = new maps.Map(elRef.current, {
          center: new maps.LatLng(
            (center && center.latitude) || 36.4,
            (center && center.longitude) || 127.8,
          ),
          zoom: Math.round((center && center.zoom) || 15),
          scaleControl: false,
          mapDataControl: false,
          logoControl: true,
          zoomControl: false,
          tileTransition: true,
        })
        mapRef.current = map

        const emitCamera = () => {
          try {
            const c = map.getCenter()
            const camera = {
              latitude: c.lat(),
              longitude: c.lng(),
              zoom: map.getZoom(),
              coveringRegion: boundsToCoveringRegion(map.getBounds()),
            }
            onCameraChange && onCameraChange(camera)
          } catch (e) {
            /* bounds not ready yet */
          }
        }
        listeners.push(maps.Event.addListener(map, 'idle', emitCamera))
        listeners.push(
          maps.Event.addListener(
            map,
            'click',
            () => onMapClick && onMapClick(),
          ),
        )
        emitCamera()
        setReady(true)
        onInitialized && onInitialized()
      })
      .catch((e) => {
        console.warn('[naver-map]', e && e.message)
      })
    return () => {
      cancelled = true
      const maps = window.naver && window.naver.maps
      if (maps) listeners.forEach((l) => maps.Event.removeListener(l))
      if (mapRef.current && mapRef.current.destroy) mapRef.current.destroy()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      animateToRegion: (region) => {
        const map = mapRef.current
        const maps = window.naver && window.naver.maps
        if (!map || !maps) return
        map.morph(
          new maps.LatLng(region.latitude, region.longitude),
          deltaToZoom(region),
        )
      },
      animateToCoordinate: (coord) => {
        const map = mapRef.current
        const maps = window.naver && window.naver.maps
        if (!map || !maps) return
        map.panTo(new maps.LatLng(coord.latitude, coord.longitude))
      },
      animateToTwoCoordinates: (c1, c2) => {
        const map = mapRef.current
        const maps = window.naver && window.naver.maps
        if (!map || !maps) return
        const bounds = new maps.LatLngBounds(
          new maps.LatLng(c1.latitude, c1.longitude),
          new maps.LatLng(c2.latitude, c2.longitude),
        )
        map.fitBounds(bounds)
      },
      // No native "location tracking mode" on web; app draws its own user marker.
      setLocationTrackingMode: () => {},
    }),
    [],
  )

  return (
    <View style={[style, styles.container]}>
      <div ref={elRef} style={{ position: 'absolute', inset: 0 }} />
      <MapContext.Provider value={ready ? mapRef.current : null}>
        {ready ? children : null}
      </MapContext.Provider>
    </View>
  )
})

function resolveIconUrl(image) {
  if (!image) return null
  if (typeof image === 'string') return image
  return image.uri || image.default || image.src || null
}

export const Marker = function Marker(props) {
  const map = useContext(MapContext)
  const markerRef = useRef(null)
  const {
    coordinate,
    image,
    width = 20,
    height = 20,
    anchor,
    caption,
    onClick,
  } = props
  const lat = coordinate && coordinate.latitude
  const lng = coordinate && coordinate.longitude
  const captionText = caption && caption.text
  const url = resolveIconUrl(image)

  useEffect(() => {
    const maps = window.naver && window.naver.maps
    if (!map || !maps || lat == null || lng == null) return
    const ax = (anchor && anchor.x != null ? anchor.x : 0.5) * width
    const ay = (anchor && anchor.y != null ? anchor.y : 1) * height
    const imgHtml = url
      ? `<img src="${url}" style="width:${width}px;height:${height}px;display:block" />`
      : `<div style="width:${width}px;height:${height}px;border-radius:50%;background:#4C68F5"></div>`
    const capHtml = captionText
      ? `<div style="position:absolute;top:${
          height + 2
        }px;left:50%;transform:translateX(-50%);font-size:11px;font-weight:700;white-space:nowrap;color:#222;text-shadow:0 0 2px #fff,0 0 2px #fff">${captionText}</div>`
      : ''
    const marker = new maps.Marker({
      position: new maps.LatLng(lat, lng),
      map,
      icon: {
        content: `<div style="position:relative">${imgHtml}${capHtml}</div>`,
        anchor: new maps.Point(ax, ay),
      },
    })
    markerRef.current = marker
    let clickListener = null
    if (onClick)
      clickListener = maps.Event.addListener(marker, 'click', onClick)
    return () => {
      if (clickListener) maps.Event.removeListener(clickListener)
      marker.setMap(null)
      markerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, lat, lng, url, width, height, captionText])

  return null
}

// Passthrough overlays we don't yet port (unused on the primary screens).
export const Path = () => null
export const Polyline = () => null
export const Polygon = () => null
export const Circle = () => null

export const Align = {
  Center: 0,
  Left: 1,
  Right: 2,
  Top: 3,
  Bottom: 4,
  TopLeft: 5,
  TopRight: 6,
  BottomLeft: 7,
  BottomRight: 8,
}

export const Coord = {}

const styles = StyleSheet.create({
  container: { overflow: 'hidden' },
})

export default NaverMapView
